import { startCase, uniqBy } from 'es-toolkit';
import React from 'react';
import DeleteIcon from '@mui/icons-material/Delete';

import { forceArray } from '@paragon/utilities';

import { SelectableItemOfType, SelectableItemType, SelectedItemIdentifier } from '../../../../library/selection/types';
import { useAppMonitor } from '../../../../monitor';
import { DeletionActionFactory, getDeletionProps } from '../../../../store/actions/deletionActions';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { SceneMenuItem } from '../common/SceneMenuItem';

export function DeleteMenuItem<T extends SelectableItemType>(props: {
  type: T;
  item: SelectableItemOfType<T> | SelectedItemIdentifier | (SelectableItemOfType<T> | SelectedItemIdentifier)[];

  userVisibleTerm?: string;
  monitorText?: string;
  onBeforeDelete?: (proceed: () => void) => void;
}) {
  const monitor = useAppMonitor();
  const dispatch = useAppDispatch();

  const text = `Delete ${props.userVisibleTerm ?? startCase(props.type)}`;

  const items = forceArray(props.item);
  if (items.some((item) => 'type' in item && item.type !== props.type)) {
    throw new Error(`DeleteMenuItem: mismatched type; expected ${props.type} for all provided items.`);
  }

  const { type } = props;
  const targetObjectHandles = uniqBy(
    forceArray(props.item).map(({ guid }) => ({ type, guid })),
    (handle) => handle.guid,
  );

  const deletionProps = useAppSelector(getDeletionProps);
  const deletionActionFactory = new DeletionActionFactory(targetObjectHandles, deletionProps);

  return (
    <SceneMenuItem
      text={`Delete ${props.userVisibleTerm ?? startCase(props.type)}`}
      icon={<DeleteIcon />}
      disabled={!deletionActionFactory.isDeletionFeasible()}
      onClick={() => {
        function performDeletion() {
          dispatch(deletionActionFactory.getDeletionAction());
          monitor.recordInteraction(
            props.monitorText ?? `${props.userVisibleTerm ?? startCase(props.type)} => ${text} menu item clicked`,
          );
        }

        if (props.onBeforeDelete) {
          props.onBeforeDelete(performDeletion);
        } else {
          performDeletion();
        }
      }}
    />
  );
}
